import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import dataMapperProfilesRoute from "./Profiles/dataMapperProfiles.Route";
import dataMapperSourceRoute from "./Source/dataMapperSource.Route";
import dataMapperBrowseRoute from "./Browse/dataMapperBrowse.Route";
import dataMapperExplorerRoute from "./Explorer/dataMapperExplorer.Route";
import dataMapperRestoreRoute from "./Restore/dataMapperRestore.Route";
import dataMapperLibraryRoute from "./Library/dataMapperLibrary.Route";
import dataMapperSchemasRoute from "./Schemas/dataMapperSchemas.Route";
import dataMapperLookupTablesRoute from "./Lookups/dataMapperLookupTables.Route";
import dataMapperMappingRoutes from "./Mapping/dataMapperMapping.Route";
import dataMapperTargetRoute from "./Target/dataMapperTarget.Route";
import dataMapperImportRoute from "./Import/dataMapperImport.Route";
import dataMapperBulkExportRoute from "./BulkExport/dataMapperBulkExport.Route";
import { withWizardFrame } from "./wizard/withWizardFrame";
import type DataMapperWizardStore from "./wizard/DataMapperWizardStore";
import { WIZARD_ROUTE } from "./wizard/wizardSteps";

/** Every Data Mapper route, in the tool's step order. */
const stepRoutes: RouteType[] = [
  ...dataMapperProfilesRoute,
  ...dataMapperSourceRoute,
  ...dataMapperRestoreRoute,
  ...dataMapperBrowseRoute,
  ...dataMapperExplorerRoute,
  // Titan profiles only (the API refuses Bulk Export for any other profile).
  ...dataMapperBulkExportRoute,
  ...dataMapperSchemasRoute,
  ...dataMapperLibraryRoute,
  ...dataMapperMappingRoutes,
  ...dataMapperLookupTablesRoute,
  ...dataMapperTargetRoute,
  ...dataMapperImportRoute,
];

const wizardStore = (rootStore: RootStore) =>
  (rootStore as unknown as { dataMapperWizardViewStore?: DataMapperWizardStore }).dataMapperWizardViewStore;

type RouteHook = (args: { rootStore: RootStore }) => void;
type LazyBody = () => Promise<{ default: React.ComponentType<object> }>;

/**
 * The Data Mapper as a wizard (2026-10-19). Each step keeps its own route, so
 * its hooks, deep links and the e2e run are unchanged, but:
 *
 * - Only the first step, "Data Mapper", is listed in the nav and page search.
 *   The step pages are reached through the wizard's stepper and Back / Next;
 *   their search words move to the first step so a search still lands there.
 * - Every body renders inside the wizard frame (stepper, the step's
 *   instructions, its page tabs, Back / Next): wizard/DataMapperWizardFrame.tsx.
 * - Every route's onEnter also reads where the tab stands (source and target
 *   status, the open profile's Titan flag) for the stepper.
 *
 * `hidden: true` is UI only, as everywhere: the step routes keep requiresAuth
 * (and Restore requiresAdmin), and the API checks the tier on every call.
 */
const dataMapperRoutesList: RouteType[] = stepRoutes.map((route) => {
  const isEntry = route.name === WIZARD_ROUTE.profiles;
  const body = route.viewportCard?.body;
  const onEnter = route.onEnter as RouteHook | undefined;
  const framed: RouteType = {
    ...route,
    onEnter: (args: { rootStore: RootStore }) => {
      onEnter?.(args);
      void wizardStore(args.rootStore)?.enter();
    },
  } as RouteType;
  if (body?.component) {
    const load = body.component as LazyBody;
    framed.viewportCard = {
      ...route.viewportCard,
      body: { ...body, component: () => load().then((m) => ({ default: withWizardFrame(m.default) })) },
    } as RouteType["viewportCard"];
  }
  if (isEntry) {
    framed.searchAliases = [
      ...new Set([...(route.searchAliases ?? []), ...stepRoutes.flatMap((r) => (r === route ? [] : (r.searchAliases ?? [])))]),
    ];
  } else {
    // A spread evaluates Bulk Export's `hidden` getter into a value; the wizard hides every step page alike.
    framed.hidden = true;
  }
  return framed;
});

export default dataMapperRoutesList;
